import { z } from 'zod';
import { PRODUCT_TYPE_CODES } from './product-types.js';
import {
  CONSENT_VALUES,
  DEFAULT_PUBLIC_STORY_TOKEN_DAYS,
  DEFAULT_STORY_CONSENT_VERSION,
  DEFAULT_STORY_PHOTO_MAX_BYTES,
  DEFAULT_STORY_VIDEO_MAX_BYTES,
  EXPIRATION_DAY_OPTIONS,
  MAX_ADMIN_NOTES_CHARS,
  MAX_DISPLAY_NAME_CHARS,
  MAX_STORY_CELEBRATION_CHARS,
  MAX_STORY_LOCATION_CHARS,
  MAX_STORY_TESTIMONIAL_CHARS,
  STORY_MEDIA_STATUSES,
  STORY_MEDIA_TYPES,
  STORY_PHOTO_CONTENT_TYPES,
  STORY_STATUSES,
  STORY_VIDEO_CONTENT_TYPES,
} from './enums.js';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((value) => (value ? value : undefined));

export const createStoryInvitationBodySchema = z
  .object({
    productName: z.string().trim().min(1).max(200),
    productType: z.enum(PRODUCT_TYPE_CODES),
    orderReference: optionalText(120),
    customerName: optionalText(120),
    feedbackRequestId: z.string().uuid().optional(),
    invoiceId: z.string().uuid().optional(),
    expirationDays: z.union([z.literal(30), z.literal(60), z.literal(90)]).optional(),
  })
  .strict();

export const publicStorySchema = z.object({
  storyId: z.string().uuid(),
  productName: z.string(),
  status: z.enum(STORY_STATUSES),
  consentVersion: z.string(),
});

export const storyDraftSchema = z.object({
  storyId: z.string().uuid(),
  status: z.enum(STORY_STATUSES),
  testimonial: z.string().optional(),
  celebrationMeaning: z.string().optional(),
  displayName: z.string().optional(),
  locationText: z.string().optional(),
  consentLevel: z.enum(CONSENT_VALUES).optional(),
  rightsConfirmation: z.boolean().optional(),
});

export const updateStoryDraftBodySchema = z
  .object({
    testimonial: optionalText(MAX_STORY_TESTIMONIAL_CHARS),
    celebrationMeaning: optionalText(MAX_STORY_CELEBRATION_CHARS),
    displayName: optionalText(MAX_DISPLAY_NAME_CHARS),
    locationText: optionalText(MAX_STORY_LOCATION_CHARS),
    consentLevel: z.enum(CONSENT_VALUES).optional(),
    rightsConfirmation: z.boolean().optional(),
  })
  .strict();

export const submitStoryBodySchema = updateStoryDraftBodySchema
  .extend({
    consentLevel: z.enum(CONSENT_VALUES),
    rightsConfirmation: z.literal(true),
  })
  .strict();

export const submitStoryResponseSchema = z.object({
  storyId: z.string().uuid(),
  status: z.literal('SUBMITTED'),
  submittedAt: z.string(),
});

export const adminStorySchema = z.object({
  storyId: z.string().uuid(),
  productName: z.string(),
  productType: z.enum(PRODUCT_TYPE_CODES),
  orderReference: z.string().optional(),
  customerName: z.string().optional(),
  feedbackRequestId: z.string().uuid().optional(),
  invoiceId: z.string().uuid().optional(),
  status: z.enum(STORY_STATUSES),
  testimonial: z.string().optional(),
  celebrationMeaning: z.string().optional(),
  displayName: z.string().optional(),
  locationText: z.string().optional(),
  consentLevel: z.enum(CONSENT_VALUES).optional(),
  rightsConfirmation: z.boolean().optional(),
  consentVersion: z.string(),
  consentedAt: z.string().optional(),
  submittedAt: z.string().optional(),
  reviewedAt: z.string().optional(),
  reviewedBy: z.string().optional(),
  rejectionReason: z.string().optional(),
  adminNotes: z.string().optional(),
  expiresAt: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
});

export const createdStoryInvitationSchema = adminStorySchema.extend({
  token: z.string().min(1),
  sharePath: z.string().min(1),
});

export const listStoriesResponseSchema = z.object({
  items: z.array(adminStorySchema),
  nextCursor: z.string().nullable(),
});

export const updateAdminStoryBodySchema = z
  .object({
    adminNotes: z.string().trim().max(MAX_ADMIN_NOTES_CHARS).optional(),
  })
  .strict();

export const rejectStoryBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export const approveStoryBodySchema = z
  .object({
    forPublication: z.boolean(),
  })
  .strict();

export const initiateStoryMediaBodySchema = z
  .object({
    mediaType: z.enum(STORY_MEDIA_TYPES),
    declaredMimeType: z.enum([...STORY_PHOTO_CONTENT_TYPES, ...STORY_VIDEO_CONTENT_TYPES]),
    sizeBytes: z.number().int().positive().max(DEFAULT_STORY_VIDEO_MAX_BYTES),
    durationMilliseconds: z.number().int().positive().max(10 * 60 * 1000).optional(),
    position: z.number().int().min(0).max(9).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.mediaType === 'PHOTO') {
      if (!(STORY_PHOTO_CONTENT_TYPES as readonly string[]).includes(value.declaredMimeType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'That photo type is not accepted.',
          path: ['declaredMimeType'],
        });
      }
      if (value.sizeBytes > DEFAULT_STORY_PHOTO_MAX_BYTES) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'That photo is larger than the 10 MB limit.',
          path: ['sizeBytes'],
        });
      }
    }
    if (value.mediaType === 'VIDEO') {
      if (!(STORY_VIDEO_CONTENT_TYPES as readonly string[]).includes(value.declaredMimeType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'That video type is not accepted.',
          path: ['declaredMimeType'],
        });
      }
    }
  });

export const publicStoryMediaSchema = z.object({
  mediaId: z.string().uuid(),
  mediaType: z.enum(STORY_MEDIA_TYPES),
  status: z.enum(STORY_MEDIA_STATUSES),
  position: z.number().int(),
  sizeBytes: z.number().int().optional(),
  durationMilliseconds: z.number().int().optional(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
});

export const initiateStoryMediaResponseSchema = z.discriminatedUnion('uploadMode', [
  z.object({
    mediaId: z.string().uuid(),
    uploadMode: z.literal('SINGLE'),
    uploadUrl: z.string().min(1),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.string(),
  }),
  z.object({
    mediaId: z.string().uuid(),
    uploadMode: z.literal('MULTIPART'),
    partSizeBytes: z.number().int(),
    maximumPartsPerRequest: z.number().int(),
    expiresAt: z.string(),
  }),
]);

export const signStoryMediaPartsBodySchema = z
  .object({
    partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(10),
  })
  .strict();

export const signStoryMediaPartsResponseSchema = z.object({
  parts: z.array(
    z.object({
      partNumber: z.number().int(),
      uploadUrl: z.string().min(1),
      headers: z.record(z.string(), z.string()),
      expiresAt: z.string(),
    }),
  ),
});

export const completeStoryMediaBodySchema = z
  .object({
    parts: z
      .array(
        z.object({
          partNumber: z.number().int().min(1).max(10_000),
          eTag: z.string().min(1),
        }),
      )
      .min(1)
      .max(10_000)
      .optional(),
  })
  .strict();

export const listPublicStoryMediaResponseSchema = z.object({
  items: z.array(publicStoryMediaSchema),
  photoCount: z.number().int(),
  readyPhotoCount: z.number().int(),
  videoCount: z.number().int(),
  readyVideoCount: z.number().int(),
});

export const adminStoryMediaSchema = publicStoryMediaSchema.extend({
  declaredMimeType: z.string().optional(),
  detectedMimeType: z.string().optional(),
  validationCode: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const listAdminStoryMediaResponseSchema = z.object({
  items: z.array(adminStoryMediaSchema),
});

export const storyMediaUrlResponseSchema = z.object({
  url: z.string().min(1),
  expiresInSeconds: z.number().int(),
});

export const rejectStoryMediaBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export {
  DEFAULT_PUBLIC_STORY_TOKEN_DAYS,
  DEFAULT_STORY_CONSENT_VERSION,
  EXPIRATION_DAY_OPTIONS,
};

export type CreateStoryInvitationBody = z.infer<typeof createStoryInvitationBodySchema>;
export type PublicStory = z.infer<typeof publicStorySchema>;
export type StoryDraft = z.infer<typeof storyDraftSchema>;
export type UpdateStoryDraftBody = z.infer<typeof updateStoryDraftBodySchema>;
export type SubmitStoryBody = z.infer<typeof submitStoryBodySchema>;
export type SubmitStoryResponse = z.infer<typeof submitStoryResponseSchema>;
export type AdminStory = z.infer<typeof adminStorySchema>;
export type CreatedStoryInvitation = z.infer<typeof createdStoryInvitationSchema>;
export type ListStoriesResponse = z.infer<typeof listStoriesResponseSchema>;
export type UpdateAdminStoryBody = z.infer<typeof updateAdminStoryBodySchema>;
export type RejectStoryBody = z.infer<typeof rejectStoryBodySchema>;
export type ApproveStoryBody = z.infer<typeof approveStoryBodySchema>;
export type InitiateStoryMediaBody = z.infer<typeof initiateStoryMediaBodySchema>;
export type PublicStoryMedia = z.infer<typeof publicStoryMediaSchema>;
export type InitiateStoryMediaResponse = z.infer<typeof initiateStoryMediaResponseSchema>;
export type SignStoryMediaPartsBody = z.infer<typeof signStoryMediaPartsBodySchema>;
export type SignStoryMediaPartsResponse = z.infer<typeof signStoryMediaPartsResponseSchema>;
export type CompleteStoryMediaBody = z.infer<typeof completeStoryMediaBodySchema>;
export type ListPublicStoryMediaResponse = z.infer<typeof listPublicStoryMediaResponseSchema>;
export type AdminStoryMedia = z.infer<typeof adminStoryMediaSchema>;
export type ListAdminStoryMediaResponse = z.infer<typeof listAdminStoryMediaResponseSchema>;
export type StoryMediaUrlResponse = z.infer<typeof storyMediaUrlResponseSchema>;
export type RejectStoryMediaBody = z.infer<typeof rejectStoryMediaBodySchema>;
